import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';

/**
 * El pulso de los últimos N días del Historial de plataformas.
 *
 * Es PRESENTACIONAL PURO: recibe los días ya normalizados y avisa cuál se eligió. No pide datos,
 * no sabe de clientes ni de la Bitácora. Así se testea sin montar media pantalla, y el día que el
 * mismo dibujo haga falta en otro lado (el Panel, el CC) se reusa sin arrastrar un fetch.
 *
 * El eje: la altura es proporcional al día MÁS ALTO de la ventana, y los días en 0 igual dibujan
 * su barra mínima. Un array corto —sólo los días con actividad— haría que 14 barras seguidas
 * signifiquen 14 días distintos según el cliente, que es exactamente donde un eje empieza a
 * mentir; por eso el backend devuelve siempre las N posiciones y acá no se compactan.
 *
 * **Cuántos eventos tiene un día se lee SIN hover.** Antes iba en un `title=""` nativo, que el
 * repo prohíbe explícitamente y que en una pantalla táctil no existe: la altura de la barra era
 * toda la información disponible, y una altura no es un número. Ahora hay dos caminos, y ninguno
 * pide un mouse quieto: el nombre accesible de cada barra (`aria-label`, que es lo que anuncia un
 * lector de pantalla y lo que aparece al tabular) y el renglón del encabezado, que dice el día y
 * su conteo cuando la barra está señalada, enfocada **o elegida** — o sea también después de un
 * toque. La misma clave para los dos: un dato que se cuenta dos veces se cuenta igual.
 */

// Altura mínima de una barra, en % del alto del gráfico. Un día en 0 tiene que verse como un día
// que pasó y no tuvo actividad, no como un hueco en el eje.
const MIN_BAR_PCT = 5;

/**
 * Normaliza la respuesta del endpoint de daily-counts a `[{date, count, hasAlert}]`.
 *
 * Devuelve `null` cuando no se pudo leer (error de red, endpoint todavía no deployado, forma
 * inesperada): el llamador distingue "no hay pulso que dibujar" de "hoy no pasó nada", y el
 * sismógrafo simplemente no se dibuja. Acepta varias envolturas a propósito — este endpoint lo
 * está construyendo otro stream y el contrato exacto puede moverse; lo que no puede pasar es que
 * la bandeja se caiga por eso.
 */
export function normalizeDailyCounts(payload) {
    const rows = extractRows(payload);
    if (!rows) return null;
    return rows.map((row, index) => normalizeRow(row, index)).filter(Boolean);
}

function extractRows(payload) {
    if (!payload) return null;
    if (Array.isArray(payload)) return payload;
    if (typeof payload !== 'object') return null;
    // `NETWORK_ERROR` / `ERROR` los inyecta APIClient; cualquier status que no sea OK no trae datos.
    if (payload.status !== undefined && payload.status !== 'OK') return null;
    const candidates = [
        payload.days,
        payload.data && payload.data.days,
        payload.data,
        payload.counts,
        payload.results,
    ];
    for (let i = 0; i < candidates.length; i += 1) {
        if (Array.isArray(candidates[i])) return candidates[i];
    }
    return null;
}

function normalizeRow(row, index) {
    if (!row || typeof row !== 'object') return null;
    const date = row.date || row.day || row.date_str || null;
    const rawCount = row.count !== undefined ? row.count : row.total;
    const count = Number.isFinite(Number(rawCount)) ? Number(rawCount) : 0;
    return {
        date,
        count,
        hasAlert: !!(row.has_alert || row.hasAlert),
        index,
    };
}

/**
 * Etiqueta corta de un día (`2026-08-14` → `14/8`). Se parte la cadena a mano en vez de
 * construir un `Date`: `new Date('2026-08-14')` se interpreta como UTC y, al oeste de Greenwich,
 * `getDate()` devuelve el día ANTERIOR. Un eje corrido un día es peor que un eje sin etiquetas.
 */
export function shortDayLabel(date) {
    if (!date || typeof date !== 'string') return '';
    const parts = date.split('-');
    if (parts.length < 3) return date;
    const day = parseInt(parts[2], 10);
    const month = parseInt(parts[1], 10);
    if (Number.isNaN(day) || Number.isNaN(month)) return date;
    return `${day}/${month}`;
}

/** El texto de un día: `14/8 · 5 eventos`. Uno solo para el nombre accesible y para el renglón. */
function dayLabelFor(day, t) {
    // `count` (y no `n`) porque el sufijo de plural de i18next se elige por ESA variable: con `n`
    // un día de un solo evento decía «1 eventos».
    return t('ia_booster.seismo.bar_title', {
        date: shortDayLabel(day.date), count: day.count,
        interpolation: {escapeValue: false},
    });
}

export function HistorySeismograph({days, selectedDate, onSelectDate}) {
    const {t} = useTranslation();
    // El día señalado con el mouse o con el teclado. Le gana al elegido: es el que el usuario está
    // apuntando ahora, y el elegido ya se anuncia aparte (la Bitácora de abajo queda filtrada).
    const [pointedDate, setPointedDate] = useState(null);
    // Fail-soft: sin datos no hay gráfico. Nada de una caja de error permanente arriba del
    // historial — el historial funciona igual sin el pulso.
    if (!days || !days.length) return null;

    const max = days.reduce((acc, day) => Math.max(acc, day.count || 0), 0);
    const last = days.length - 1;

    const readoutDate = pointedDate || selectedDate;
    const readoutDay = readoutDate ? days.find(day => day.date === readoutDate) : null;

    return (
        <div className="iabm-seismo">
            <div className="iabm-seismo__head">
                <h4 className="iabm-seismo__title">
                    {t('ia_booster.seismo.title', {n: days.length})}
                </h4>
                {/* El renglón enseña a leer el gráfico hasta que hay un día señalado; ahí pasa a
                    decir el dato de ESE día. Reusa el MISMO slot en vez de sumar un elemento que
                    aparece y desaparece, que empujaría las barras de abajo cada vez que el mouse
                    entra a una. */}
                <span className="iabm-seismo__hint">
                    {readoutDay ? dayLabelFor(readoutDay, t) : t('ia_booster.seismo.hint')}
                </span>
            </div>
            <div className="iabm-seismo__bars">
                {days.map((day, index) => {
                    const isOn = !!day.date && day.date === selectedDate;
                    const pct = max > 0
                        ? Math.max(MIN_BAR_PCT, Math.round((day.count / max) * 100))
                        : MIN_BAR_PCT;
                    // La primera, la última y una de cada dos: con 14 etiquetas seguidas se pisan
                    // entre ellas y no se lee ninguna.
                    const showLabel = index === 0 || index === last || index % 2 === 0;
                    // Un día sin eventos no se puede abrir: clickearlo fijaba el día y dejaba la
                    // bandeja vacía con el cartel «Mostrando solo el 8/9» sobre una lista que no
                    // tiene nada que mostrar. Es un click que sólo puede decepcionar. La barra
                    // sigue dibujándose (un día en cero es un día que pasó, no un hueco en el eje)
                    // y sigue anunciando su conteo — deja de ser un destino, no de ser un dato.
                    const empty = !day.count && !isOn;
                    return (
                        <button
                            key={day.date || index}
                            type="button"
                            className={`iabm-seismo__bar ${isOn ? '-on' : ''} ${empty ? '-empty' : ''}`}
                            disabled={empty}
                            aria-pressed={isOn}
                            aria-label={dayLabelFor(day, t)}
                            onMouseEnter={() => setPointedDate(day.date)}
                            onMouseLeave={() => setPointedDate(null)}
                            onFocus={() => setPointedDate(day.date)}
                            onBlur={() => setPointedDate(null)}
                            onClick={() => onSelectDate && onSelectDate(isOn ? null : day.date)}
                        >
                            <span className="iabm-seismo__fill" style={{height: `${pct}%`}}>
                                {day.hasAlert && <span className="iabm-seismo__alert"/>}
                            </span>
                            <span className="iabm-seismo__day">
                                {showLabel ? shortDayLabel(day.date) : ''}
                            </span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

export default HistorySeismograph;
